import { reset, wait } from 'src/store'
import { get } from 'src/axios/post'

// Limit amount of saved client IDs for each payload
const SUGGESTION_LIMIT = 10

const loggedInStates = () => {
  return [
    'loadingAddressesForClient',
    'addresses',

    'loadingEmailsForClient',
    'emails',

    'loadingPhonesForClient',
    'phones'
  ]
}

// This VueX store can store up to X suggestions for client profiles
// Used on input fields which supply suggestions of values to set in its input box
const defaultStates = () => {
  return {
    loadingAddressesForClient: null,
    addresses: {},

    loadingEmailsforClient: null,
    emails: {},

    loadingPhonesForClient: null,
    phones: {}
  }
}

export default {
  namespaced: true,
  state: defaultStates(),
  mutations: {
    reset (state) {
      reset(state, loggedInStates(), defaultStates())
    },

    // Addresses
    clearAddresses (state) {
      state.addresses = {}
    },
    setAddressesLoading (state, clientId) {
      state.loadingAddressesForClient = clientId
    },
    removeClientAddresses (state, clientId) {
      if (!clientId) {
        return
      }
      delete state.addresses[clientId]
    },
    addClientAddresses (state, { clientId, values = [] }) {
      if (!clientId) {
        return
      }
      state.addresses[clientId] = values
    },

    // Emails
    clearEmails (state) {
      state.emails = {}
    },
    setEmailsLoading (state, clientId) {
      state.loadingEmailsforClient = clientId
    },
    removeClientEmails (state, clientId) {
      if (!clientId) {
        return
      }
      delete state.emails[clientId]
    },
    addClientEmails (state, { clientId, values = [] }) {
      if (!clientId) {
        return
      }
      state.emails[clientId] = values
    },

    // Phones
    clearPhones (state) {
      state.phones = {}
    },
    setPhonesLoading (state, clientId) {
      state.loadingPhonesForClient = clientId
    },
    removeClientPhones (state, clientId) {
      if (!clientId) {
        return
      }
      delete state.phones[clientId]
    },
    addClientPhones (state, { clientId, values = [] }) {
      if (!clientId) {
        return
      }
      state.phones[clientId] = values
    }
  },
  actions: {
    // Addresses
    async loadAddresses ({ commit, state }, clientId) {
      if (!clientId || state.addresses[clientId]) {
        return
      }

      if (state.loadingAddressesForClient) {
        await wait(state, 'loadingAddressesForClient')

        if (state.addresses[clientId]) {
          return
        }
      }

      if (Object.keys(state.addresses).length >= SUGGESTION_LIMIT && !state.addresses[clientId]) {
        await commit('clearAddresses')
      }

      commit('setAddressesLoading', clientId)
      const { status, payload } = await get('v1/clients/addresses', {
        id: clientId
      })
      commit('setAddressesLoading', null)
      if (status === 'success') {
        commit('addClientAddresses', { clientId, values: payload.addresses })
      }
    },

    // Emails
    async loadEmails ({ commit, state }, clientId) {
      if (!clientId || state.emails[clientId]) {
        return
      }

      if (state.loadingEmailsforClient) {
        await wait(state, 'loadingEmailsForClient')

        if (state.emails[clientId]) {
          return
        }
      }

      if (Object.keys(state.emails).length >= SUGGESTION_LIMIT && !state.emails[clientId]) {
        await commit('clearEmails')
      }

      commit('setEmailsLoading', clientId)
      const { status, payload } = await get('v1/clients/emails', {
        id: clientId
      })
      commit('setEmailsLoading', null)
      if (status === 'success') {
        commit('addClientEmails', { clientId, values: payload.emails })
      }
    },

    // Phones
    async loadPhones ({ commit, state }, clientId) {
      if (!clientId || state.phones[clientId]) {
        return
      }

      if (state.loadingPhonesForClient) {
        await wait(state, 'loadingPhonesForClient')

        if (state.phones[clientId]) {
          return
        }
      }

      if (Object.keys(state.phones).length >= SUGGESTION_LIMIT && !state.phones[clientId]) {
        await commit('clearPhones')
      }

      commit('setPhonesLoading', clientId)
      const { status, payload } = await get('v1/clients/phones', {
        id: clientId
      })
      commit('setPhonesLoading', null)
      if (status === 'success') {
        commit('addClientPhones', { clientId, values: payload.phones })
      }
    }
  },
  getters: {
    // Addresses
    getAddresses: state => (clientId) => state.addresses[clientId] || [],
    isLoadingAddresses: state => (clientId) => clientId && state.loadingAddressesForClient === clientId,

    // Emails
    getEmails: state => (clientId) => state.emails[clientId] || [],
    isLoadingEmails: state => (clientId) => clientId && state.loadingEmailsforClient === clientId,

    // Phones
    getPhones: state => (clientId) => state.phones[clientId] || [],
    isLoadingPhones: state => (clientId) => clientId && state.loadingPhonesForClient === clientId
  }
}
